import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { DetailedOffer as DetailedOfferPreview } from '../../../components/PrivateOfferForm/DetailedOffer/DetailedOffer'
import type { DetailedOffer } from '../../../components/PrivateOfferForm/DetailedOffer/DetailedOffer'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import {
  ButtonV2,
  DateTime,
  LabraToolTip,
  ModalConfirm,
  ModalSizeVariants,
  TableComponent,
} from '@labrav/react-components'
import { isEmpty } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  clearResaleAuthorizations,
  getResellerAgreementData,
  setResaleAuthorizationsPageNumber,
  updateAgreementId,
  updateAgreementStatus,
  updateResaleAuthorizationsPageSize,
  updateResellerData,
  type ResaleAuthorizationsResellerAgreements,
  type ResaleAuthorizationsState,
  ResellerAgreementStatus,
} from '../../modules/resaleAuthorizations'
import { useDispatch, useSelector } from '../../../store'
import { useStyles } from './ResaleAuthorizationsList.styles'
import { get } from 'lodash'
import {
  PartnerType,
  useAccountId,
  useCurrentCloudProvider,
  useUserType,
} from '@labrav/common'
import { VisibilityOutlined } from '@mui/icons-material'
import { getKeyFromValue } from '../../../common/utils/getKeyFromValue'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'
import { ShortenedName } from '../../../common/components/ShortenedName/ShortenedName'
import { Link, useParams } from 'react-router-dom'
import { getPricesArray } from '../../../modules/resaleAuthorization/utils/getPricesArray'
import type { PriceTypes } from '../../../types/flyout'
import { useCommonStyles } from '../../../common/styles/common.styles'
import type { CloudMarketplace } from '../../../types/index'
import { getResaleOfferForDetailedView } from './helpers'
import {
  mappingResaleForAdmin,
  mappingResaleForLabraUI,
  statusMappingForLabraUI,
} from './constants'
import type { ResaleAuthorizationsListProps } from './types'

export type { ResaleAuthorizationsListProps } from './types'
export { getIsCustomPricing, getResaleOfferForDetailedView } from './helpers'
export { mappingResaleForAdmin, mappingResaleForLabraUI } from './constants'

export const ResaleAuthorizationsList: React.FC<
  ResaleAuthorizationsListProps
> = ({ crmOpportunityId, externalBaseURL, search }) => {
  const { classes } = useStyles()
  const { classes: commonClasses } = useCommonStyles()
  const { products, selectedProduct: productId } =
    useFlyOutProductsBasedOnSelectedCloud()
  const isAdmin = useUserType() === PartnerType.Admin
  const { partner_id: accountIdFromParams } = useParams<{
    partner_id?: string
  }>()
  const selectedCloudTypeProductListing =
    useCurrentCloudProvider() as CloudMarketplace
  const accountId = (useAccountId({ product: 'flyout' }) ||
    accountIdFromParams ||
    '') as string
  const [offerData, setOfferData] = useState<DetailedOffer>({} as DetailedOffer)
  const dispatch = useDispatch()
  const { resellerAgreements, pageSize, pageNumber, noOfPage } =
    useSelector<ResaleAuthorizationsState>(state => state.cppoReseller)
  const pricesArray = (
    offerData?.isCustomPricing
      ? ['price']
      : getPricesArray(get(offerData, 'dimensions', []))
  ) as [keyof PriceTypes]
  const { timeZone } = useTimeZone()
  useEffect(() => {
    if (!externalBaseURL && productId !== undefined && !isEmpty(products))
      dispatch(
        getResellerAgreementData({
          partnerId: accountId,
          pageSize,
          pageNumber: 1,
          productId,
          crmOpportunityId,
          cloud: selectedCloudTypeProductListing,
        })
      )
    return () => {
      dispatch(clearResaleAuthorizations())
    }
  }, [accountId, productId])

  const noOp = useCallback(() => void 0, [])
  const onChangeRowsPerPage = useCallback(
    (e: SelectChangeEvent) => {
      dispatch(setResaleAuthorizationsPageNumber(1))
      if (accountId !== null) {
        const noOfRows = Number(e.target.value)
        dispatch(clearResaleAuthorizations())
        dispatch(
          updateResaleAuthorizationsPageSize({
            partnerId: accountId,
            pageSize: noOfRows,
            productId: productId || '',
            crmOpportunityId,
            cloud: selectedCloudTypeProductListing,
          })
        )
      }
    },
    [accountId, productId, crmOpportunityId]
  )
  const handlePageChange = useCallback(
    (e: React.ChangeEvent<unknown>, pageNumber: number) => {
      dispatch(setResaleAuthorizationsPageNumber(pageNumber))
      dispatch(clearResaleAuthorizations())
      if (accountId !== null) {
        dispatch(
          getResellerAgreementData({
            partnerId: accountId,
            pageSize,
            pageNumber,
            productId,
            crmOpportunityId,
            cloud: selectedCloudTypeProductListing,
          })
        )
      }
    },
    [accountId, pageSize, productId, crmOpportunityId]
  )
  const [openModal, setOpenModal] = useState<boolean>(false)
  const viewCPPODetailed = useCallback(
    (agreement: ResaleAuthorizationsResellerAgreements) => () => {
      const offer = getResaleOfferForDetailedView(agreement, timeZone)
      setOfferData(offer)
      setOpenModal(true)
    },
    []
  )
  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [openModal])

  const updateCPPOData = useCallback(
    (agreementId: string) => async () => {
      await dispatch(updateResellerData(accountId, agreementId))
    },
    []
  )
  const updateStatus = useCallback(
    (agreementId: string) => (event: SelectChangeEvent) => {
      dispatch(
        updateAgreementStatus(
          agreementId,
          (event?.target?.value as ResellerAgreementStatus) ||
            ResellerAgreementStatus.IN_PROGRESS
        )
      )
    },
    [resellerAgreements]
  )
  const updateId = useCallback(
    (agreementId: string) =>
      async (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) => {
        dispatch(updateAgreementId(agreementId, event?.target?.value || ''))
      },
    [resellerAgreements]
  )
  const getCustomObjectForAdmin = () =>
    resellerAgreements.map(agreement => {
      return {
        resellerName: get(
          agreement,
          'resellersInfo[0].resellerCompanyName',
          '-'
        ),
        id: agreement.id,
        productName: (
          <ShortenedName
            name={get(agreement, 'products[0].productName', '-')}
          />
        ) as never,
        createdAt: (<DateTime date={agreement.createdAt} />) as never,
        awsAgreementId: (
          <TextField
            onChange={updateId(agreement.id)}
            key={'awsAgreementId'}
            id={`awsAgreementId-${agreement.id}`}
            label={'Enter AWS agreement ID'}
            placeholder="N/A"
            defaultValue={agreement.awsAgreementId}
            variant="standard"
            className={`${classes.resizeFontTextField} ${classes.defaultValueWidth}`}
            slotProps={{
              htmlInput: {
                'data-testid': `agreement-id-${agreement.id}`,
                'data-id': `agreement-id-${agreement.id}`,
              },
            }}
          />
        ) as never,
        status: (
          <FormControl>
            <InputLabel classes={{ shrink: commonClasses.shrink }}>
              Agreement status
            </InputLabel>
            <Select
              labelId="Agreement Status"
              label="Agreement status"
              defaultValue={
                agreement?.status ?? ResellerAgreementStatus.IN_PROGRESS
              }
              onChange={updateStatus(agreement.id)}
              inputProps={{ 'data-testid': 'input' }}
              className={classes.selectField}
              data-testid={`status-${agreement.id}`}
              variant="standard"
            >
              {Object.keys(statusMappingForLabraUI).map(status => (
                <MenuItem
                  data-testid="menu-item"
                  value={statusMappingForLabraUI[status]}
                  className={classes.menuRoot}
                  key={status}
                >
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) as never,
        actions: (
          <div className={classes.buttonContainer}>
            <ButtonV2
              className={classes.submitButton}
              onClick={updateCPPOData(agreement.id)}
              data-testid={`send-details-${agreement.id}`}
            >
              Submit
            </ButtonV2>
            <ButtonV2
              className={classes.submitButton}
              onClick={viewCPPODetailed(agreement)}
              data-testid={`view-details-${agreement.id}`}
            >
              Details
            </ButtonV2>
          </div>
        ) as never,
      }
    })
  const baseUrl = useMemo(
    () => externalBaseURL || '/flyout/products-listing',
    [externalBaseURL]
  )
  const getCustomObjectForLabraUI = () =>
    resellerAgreements.map(agreement => {
      const productId =
        get(agreement, 'products[0].awsProductId', '') || agreement.awsProductId
      const productDetail = products.find(
        productItem => productItem.productId === productId
      )
      const draftUrl = `/resale-authorizations/${agreement.id}/edit`
      const viewUrl = `/resale-authorizations/${agreement.id}/details`
      const pathname = `${baseUrl}/${productId}${
        agreement.draft ? draftUrl : viewUrl
      }`
      return {
        resellerName: get(
          agreement,
          'resellersInfo[0].resellerCompanyName',
          '-'
        ),
        labraAuthorizationId: (
          <ShortenedName name={agreement.id} length={16} />
        ) as never,
        awsAuthorizationId: agreement.awsAgreementId,
        productName: (
          <ShortenedName
            name={
              get(agreement, 'products[0].productName', '') ||
              productDetail?.productName ||
              '-'
            }
            length={21}
          />
        ) as never,
        createdAt: (<DateTime date={agreement.createdAt} />) as never,
        authorisationName: (
          <ShortenedName name={agreement.agreementName} length={16} />
        ),
        status:
          getKeyFromValue(
            agreement?.status || ResellerAgreementStatus.IN_PROGRESS
          )(statusMappingForLabraUI) || '',
        actions: (
          <div className={classes.actionIcon}>
            <Link
              to={{
                pathname,
                search,
              }}
              className={classes.contractLink}
              data-id="link-to-private-offer-details"
            >
              <LabraToolTip
                label={'View Resale Authorization details'}
                tooltipPlacement="top"
              >
                <VisibilityOutlined
                  fontSize="small"
                  data-testid={`visibility-${agreement.id}`}
                  className={classes.actionIcon}
                />
              </LabraToolTip>
            </Link>
          </div>
        ) as never,
      }
    })
  const objectLabraUI = isAdmin
    ? getCustomObjectForAdmin()
    : getCustomObjectForLabraUI()
  return (
    <div data-testid="resale-auth-list">
      <TableComponent
        mapping={isAdmin ? mappingResaleForAdmin : mappingResaleForLabraUI}
        objects={objectLabraUI}
        component="div"
        hidePagination={false}
        onChange={handlePageChange}
        onClick={noOp}
        onChangeRowsPerPage={onChangeRowsPerPage}
        noHover={true}
        rowsPerPageOptions={[10, 20, 30]}
        pageNumber={pageNumber}
        count={noOfPage || 0}
        rowsPerPage={pageSize}
      />
      <ModalConfirm
        title={'Preview'}
        subTitle={''}
        content={
          <DetailedOfferPreview offer={offerData} pricesArray={pricesArray} />
        }
        open={openModal}
        titleAcceptButton={'Okay'}
        onAccept={handleClose}
        onDecline={handleClose}
        variant={ModalSizeVariants.medium}
        hideCancelButton={true}
      />
    </div>
  )
}
